import jsPDF from 'jspdf';

/**
 * Beslutsbrev PDF Generator - Professional Edition
 * 
 * Clean formatting with proper text wrapping
 */

// Brand colors
const COLORS = {
  forest800: [34, 84, 61],
  forest700: [21, 87, 54],
  forest600: [22, 101, 52],
  forest50: [240, 253, 244],
  green700: [21, 128, 61],
  blue50: [239, 246, 255],
  blue700: [29, 78, 216],
  red500: [239, 68, 68],
  red50: [254, 242, 242],
  amber500: [245, 158, 11],
  amber50: [254, 249, 195],
  gray700: [55, 65, 81],
  gray500: [107, 114, 128],
  gray400: [156, 163, 175],
  gray300: [209, 213, 219],
  white: [255, 255, 255]
};

const DATA_PERIOD = 'Q1 2026';

export const generateBeslutsbrev = (result, inputs, language = 'sv') => {
  const doc = new jsPDF();
  
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 18;
  const contentWidth = pageWidth - (margin * 2);
  const maxY = pageHeight - 25;
  let y = 20;
  let pageNum = 1;

  const checkPageBreak = (needed = 20) => {
    if (y + needed > maxY) {
      addFooter();
      doc.addPage();
      pageNum++;
      y = 20;
    }
  };

  const addFooter = () => {
    doc.setDrawColor(...COLORS.gray300);
    doc.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.gray500);
    doc.text('Skogskalkyl.se', margin, pageHeight - 10);
    doc.text(`Sida ${pageNum}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
  };

  const addSection = (title) => {
    checkPageBreak(20);
    doc.setFillColor(...COLORS.forest800);
    doc.roundedRect(margin, y, contentWidth, 8, 1.5, 1.5, 'F');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.white);
    doc.text(title, margin + 3, y + 5.5);
    y += 12;
  };

  // Calculations
  const region = inputs?.region || 'svealand';
  const areal = inputs?.areal || 50;
  const volym = inputs?.virkesvolym || 7500;
  const midValue = result?.midEstimate || 0;
  const pricePerHa = areal > 0 ? midValue / areal : 0;
  const volymPerHa = areal > 0 ? volym / areal : 0;

  const regionNames = { norrland: 'norra Sverige', svealand: 'mellersta Sverige', gotaland: 'södra Sverige' };
  const benchmarks = { norrland: 65000, svealand: 85000, gotaland: 110000 };
  const benchmark = benchmarks[region] || 85000;
  const isAbove = pricePerHa > benchmark * 1.1;
  const isBelow = pricePerHa < benchmark * 0.9;

  // ===================
  // HEADER
  // ===================
  doc.setFillColor(...COLORS.forest800);
  doc.rect(0, 0, pageWidth, 32, 'F');
  
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.white);
  doc.text('Skogskalkyl', margin, 12);
  
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(200, 220, 200);
  doc.text('Från siffra till bättre beslut', margin, 18);
  
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.white);
  doc.text('BESLUTSBREV', pageWidth - margin, 14, { align: 'right' });
  
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(200, 220, 200);
  doc.text(`Datum: ${new Date().toLocaleDateString('sv-SE')}  |  Prisdata: ${DATA_PERIOD}`, pageWidth - margin, 22, { align: 'right' });
  
  y = 40;

  // ===================
  // WARNING BOX
  // ===================
  doc.setFillColor(...COLORS.amber50);
  doc.setDrawColor(...COLORS.amber500);
  doc.setLineWidth(0.4);
  doc.roundedRect(margin, y, contentWidth, 10, 2, 2, 'FD');
  
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(120, 53, 15);
  doc.text('OBS: INDIKATIV UPPSKATTNING – ersätter inte auktoriserad värdering vid köp, försäljning eller belåning.', margin + 3, y + 6);
  y += 15;

  // ===================
  // SUMMARY BOX
  // ===================
  doc.setFillColor(248, 250, 248);
  doc.setDrawColor(...COLORS.forest600);
  doc.setLineWidth(0.4);
  doc.roundedRect(margin, y, contentWidth, 18, 2, 2, 'FD');
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.forest800);
  doc.text(`Skogsfastighet: ${areal} ha i ${regionNames[region]}`, margin + 4, y + 7);
  
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.gray500);
  doc.text(`Virkesförråd: ${volym.toLocaleString('sv-SE')} m³sk (${volymPerHa.toFixed(0)} m³sk/ha)`, margin + 4, y + 13);
  
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.green700);
  doc.text(`${(midValue / 1000000).toFixed(2)} MSEK`, pageWidth - margin - 4, y + 11, { align: 'right' });
  y += 24;

  // ===================
  // 1. ÄRENDE
  // ===================
  addSection('1. Ärende');
  
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.gray700);
  doc.text(`Objekt: Skogsfastighet, ${areal} ha produktiv skogsmark i ${regionNames[region]}`, margin + 2, y);
  y += 5;
  doc.text(`Virkesförråd: ${volym.toLocaleString('sv-SE')} m³sk (angivet av användaren, ej verifierat)`, margin + 2, y);
  y += 5;
  doc.text('Syfte: Indikativ värdering för internt beslutsunderlag', margin + 2, y);
  y += 8;

  // ===================
  // 2. RESULTAT
  // ===================
  addSection('2. Indikerat värde');
  
  checkPageBreak(25);
  doc.setFillColor(...COLORS.forest50);
  doc.roundedRect(margin, y, contentWidth, 16, 2, 2, 'F');
  
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.forest800);
  doc.text(`${(midValue / 1000000).toFixed(2)} MSEK`, margin + contentWidth/2, y + 7, { align: 'center' });
  
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.gray500);
  doc.text(`Spann: ${(result?.lowEstimate / 1000000).toFixed(2)} – ${(result?.highEstimate / 1000000).toFixed(2)} MSEK (±15%)`, margin + contentWidth/2, y + 13, { align: 'center' });
  y += 22;

  doc.setFontSize(8);
  doc.setTextColor(...COLORS.gray700);
  doc.text(`Pris per hektar: ${Math.round(pricePerHa).toLocaleString('sv-SE')} kr/ha`, margin + 2, y);
  doc.text(`Regionsnitt: ca ${benchmark.toLocaleString('sv-SE')} kr/ha`, margin + 90, y);
  y += 5;
  
  const posText = isAbove ? 'Över regionsnitt (+10%)' : isBelow ? 'Under regionsnitt (-10%)' : 'Nära regionsnitt';
  doc.text(`Jämförelse: ${posText}`, margin + 2, y);
  y += 8;

  // ===================
  // 3. METODIK
  // ===================
  addSection('3. Beräkningsmetodik');
  
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.gray700);
  const methodLines = doc.splitTextToSize(
    'Värderingen bygger på en förenklad ortsprismetod. Fastighetens uppskattade virkesförråd multipliceras med regionala genomsnittspriser per m³sk. Metoden ger en grov indikation men beaktar inte bonitet, åldersfördelning, arrondering, vägnät eller lokala marknadsförhållanden.',
    contentWidth - 4
  );
  doc.text(methodLines, margin + 2, y);
  y += methodLines.length * 4 + 4;

  // ===================
  // 4. OSÄKERHETER
  // ===================
  addSection('4. Väsentliga osäkerheter');
  
  const uncertainties = [
    { factor: 'Virkesförråd ej verifierat', impact: 'Hög', desc: 'Kan ändra värdet ±20%' },
    { factor: 'Bonitet/tillväxt okänd', impact: 'Medel', desc: 'Påverkar avkastning' },
    { factor: 'Åldersfördelning okänd', impact: 'Medel', desc: 'Avgör avverkningsmöjligheter' },
    { factor: 'Lokala marknadsförhållanden', impact: 'Medel', desc: 'Köptryck varierar' }
  ];

  uncertainties.forEach((item) => {
    checkPageBreak(8);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.gray700);
    doc.text(`• ${item.factor}`, margin + 2, y);
    
    // Badge
    const badgeColor = item.impact === 'Hög' ? COLORS.red500 : COLORS.amber500;
    doc.setFillColor(...badgeColor);
    doc.roundedRect(margin + 55, y - 3, 12, 4.5, 1, 1, 'F');
    doc.setFontSize(6);
    doc.setTextColor(...COLORS.white);
    doc.setFont('helvetica', 'bold');
    doc.text(item.impact, margin + 57, y - 0.5);
    
    // Description
    doc.setFontSize(7);
    doc.setTextColor(...COLORS.gray500);
    doc.setFont('helvetica', 'normal');
    doc.text(item.desc, margin + 70, y);
    y += 6;
  });
  y += 4;

  // ===================
  // 5. BESLUTSBEHOV
  // ===================
  addSection('5. Räcker detta underlag?');
  
  checkPageBreak(30);
  
  const decisions = [
    { label: 'Första prisindikation', ok: true },
    { label: 'Intern diskussion', ok: true },
    { label: 'Kontakta mäklare', ok: true },
    { label: 'Slutligt säljbeslut', ok: false },
    { label: 'Bankärende', ok: false },
    { label: 'Arvskifte', ok: false }
  ];

  doc.setFontSize(8);
  decisions.forEach((item, idx) => {
    const col = idx % 3;
    const row = Math.floor(idx / 3);
    const xPos = margin + 2 + (col * 58);
    const yPos = y + (row * 6);
    
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.gray700);
    doc.text(`${item.label}:`, xPos, yPos);
    
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(item.ok ? 34 : 200, item.ok ? 150 : 50, item.ok ? 34 : 50);
    doc.text(item.ok ? 'JA' : 'NEJ', xPos + 40, yPos);
  });
  y += 16;
  
  // Recommendation
  checkPageBreak(15);
  doc.setFillColor(...COLORS.blue50);
  doc.roundedRect(margin, y, contentWidth, 10, 2, 2, 'F');
  doc.setFontSize(7);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(...COLORS.blue700);
  const rec = isAbove 
    ? 'Rekommendation: Inhämta 2-3 värderingar från auktoriserade skogsmäklare vid försäljningsintresse.'
    : 'Rekommendation: Verifiera virkesförrådet med aktuell skogsbruksplan innan vidare beslut.';
  doc.text(rec, margin + 3, y + 6);
  y += 15;

  // ===================
  // 6. NÄSTA STEG
  // ===================
  addSection('6. Föreslagna nästa steg');
  
  const steps = [
    '1. Verifiera virkesförrådet – beställ eller uppdatera skogsbruksplan',
    '2. ' + (isAbove ? 'Kontakta 2-3 skogsmäklare för kostnadsfri värdering' : 'Bestäm tidsperspektiv: 1, 3 eller 5+ år'),
    '3. Granska ägarsituation, lagfart och eventuella servitut'
  ];
  
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.gray700);
  steps.forEach((step) => {
    checkPageBreak(6);
    doc.text(step, margin + 2, y);
    y += 5;
  });
  y += 5;

  // ===================
  // 7. KÄLLOR
  // ===================
  addSection('7. Datakällor');
  
  doc.setFontSize(7);
  doc.setTextColor(...COLORS.gray500);
  const sources = [
    `• Prisstatistik: Regionala genomsnittspriser (${DATA_PERIOD}), baserat på Ludvig & Co, LRF Konsult`,
    '• Skogsstyrelsen: Officiell skogsstatistik',
    '• SLU Riksskogstaxeringen: Virkesförråd och tillväxtdata'
  ];
  sources.forEach((src) => {
    doc.text(src, margin + 2, y);
    y += 4;
  });
  y += 5;

  // ===================
  // FINAL DISCLAIMER
  // ===================
  checkPageBreak(20);
  doc.setFillColor(...COLORS.red50);
  doc.setDrawColor(...COLORS.red500);
  doc.setLineWidth(0.3);
  doc.roundedRect(margin, y, contentWidth, 16, 2, 2, 'FD');
  
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(150, 30, 30);
  doc.text('VIKTIG INFORMATION', margin + 3, y + 5);
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6);
  const discText = doc.splitTextToSize(
    'Detta beslutsbrev är en indikativ uppskattning, inte en officiell värdering. Dokumentet ska inte användas som enda underlag för köp, försäljning, belåning eller juridiska beslut. För bindande värdering, kontakta en auktoriserad fastighetsvärderare.',
    contentWidth - 6
  );
  doc.text(discText, margin + 3, y + 9);

  addFooter();
  return doc;
};

export const downloadBeslutsbrev = (result, inputs, language = 'sv') => {
  const doc = generateBeslutsbrev(result, inputs, language);
  doc.save(`Beslutsbrev_${inputs?.areal || 50}ha_${new Date().toISOString().split('T')[0]}.pdf`);
};

export const printBeslutsbrev = () => window.print();

export default { generateBeslutsbrev, downloadBeslutsbrev, printBeslutsbrev };
